import { useState } from 'react';
import { Heart, Plus, Minus, Star, Loader2, Check } from 'lucide-react';
import useWishlist from '../../hooks/useWishlist';
import { addToCart as apiAddToCart } from '../../utils/api';

function StarRow({ rating = 0, count = 0 }) {
  return (
    <div className="flex items-center gap-1 mb-2">
      {[1,2,3,4,5].map((n) => (
        <Star
          key={n}
          className={`w-3.5 h-3.5 ${n <= Math.round(rating) ? 'fill-secondary text-secondary' : 'text-gray-200 fill-gray-200'}`}
        />
      ))}
      {count > 0 && <span className="text-gray-400 text-xs ml-1">({count})</span>}
    </div>
  );
}

const currency = () => window.themeData?.currency ?? '$';

export default function ShopProductCard({ product, index = 0 }) {
  const { toggle: toggleWishlist, isWishlisted } = useWishlist();
  const productId  = product?.id;
  const wishlisted = isWishlisted(productId);
  const [qty,        setQty]        = useState(1);
  const [adding,     setAdding]     = useState(false);
  const [added,      setAdded]      = useState(false);

  const addToCart = async () => {
    if (adding) return;
    setAdding(true);
    try {
      await apiAddToCart(productId, qty);
      setAdded(true);
      setTimeout(() => setAdded(false), 2000);
    } catch (e) {
      console.error('Add to cart failed', e);
    } finally {
      setAdding(false);
    }
  };

  const name         = product?.name       ?? 'Product';
  const price        = parseFloat(product?.price        ?? 0);
  const regularPrice = parseFloat(product?.regular_price ?? 0);
  const onSale       = product?.on_sale && regularPrice > price;
  const featured     = product?.featured;
  const rating       = parseFloat(product?.average_rating ?? 0);
  const ratingCount  = product?.rating_count ?? 0;
  const image        = product?.images?.[0]?.src;
  const permalink    = product?.permalink ?? '#';
  const badge        = onSale
    ? { label: `${Math.round((1 - price / regularPrice) * 100)}% OFF`, cls: 'bg-secondary text-textDark' }
    : featured
    ? { label: 'HOT', cls: 'bg-accent1 text-white' }
    : product?.date_created?.startsWith(new Date().getFullYear().toString())
    ? { label: 'NEW', cls: 'bg-accent3 text-white' }
    : null;

  return (
    <div className="bg-white rounded-3xl border border-gray-100 shadow-sm hover:shadow-xl transition-all duration-300 overflow-hidden group relative flex flex-col">
      {/* Badge */}
      {badge && (
        <div className="absolute top-4 left-4 z-10">
          <span className={`${badge.cls} text-xs font-black px-2.5 py-1 rounded-lg shadow-sm`}>{badge.label}</span>
        </div>
      )}

      {/* Wishlist */}
      <button
        onClick={() => toggleWishlist(productId)}
        className={`absolute top-4 right-4 z-10 w-9 h-9 rounded-full backdrop-blur-sm flex items-center justify-center shadow-sm transition-colors ${wishlisted ? 'bg-accent1 text-white' : 'bg-white/90 text-gray-400 hover:text-accent1'}`}
      >
        <Heart className={`w-4 h-4 ${wishlisted ? 'fill-current' : ''}`} />
      </button>

      {/* Image area */}
      <a href={permalink} className="block">
        <div className="h-52 bg-gray-50 flex items-center justify-center p-6 overflow-hidden group-hover:bg-gray-100 transition-colors cursor-pointer">
          {image
            ? <img
                src={image}
                alt={name}
                className="h-full w-full object-contain group-hover:scale-110 transition-transform duration-500"
              />
            : <span className="text-7xl group-hover:scale-110 transition-transform duration-300">🎁</span>
          }
        </div>
      </a>

      {/* Info */}
      <div className="p-5 flex flex-col flex-grow">
        <StarRow rating={rating} count={ratingCount} />

        <a href={permalink}>
          <h3 className="font-bold text-textDark text-base mb-1 line-clamp-2 hover:text-primary transition-colors cursor-pointer">
            {name}
          </h3>
        </a>

        <div className="mt-auto pt-4 border-t border-gray-50 flex items-center justify-between">
          <div>
            <span className="font-black text-xl text-primary">{currency()}{price.toFixed(2)}</span>
            {onSale && (
              <span className="text-sm text-gray-400 line-through ml-2">{currency()}{regularPrice.toFixed(2)}</span>
            )}
          </div>
          <button
            onClick={addToCart}
            disabled={adding}
            className={`w-10 h-10 rounded-xl flex items-center justify-center transition-colors shadow-sm disabled:opacity-70 disabled:cursor-not-allowed ${added ? 'bg-accent3 text-white' : 'bg-primary/10 text-primary hover:bg-primary hover:text-white'}`}
          >
            {adding
              ? <Loader2 className="w-4 h-4 animate-spin" />
              : added
              ? <Check className="w-4 h-4" />
              : <Plus className="w-4 h-4" />
            }
          </button>
        </div>
      </div>
    </div>
  );
}
